/**
 * Platform scope — the primitive behind "one set of core engines, many
 * platforms" (spec customer-success/v1.5.1/001 §2.1, README S1/S2/S12).
 *
 * A scope is a PRODUCT SURFACE inside an organisation: `insurance-broker` is the broker
 * product, `customer` is the Customer section (`/cs`), `underwriter` is the
 * Underwriter product (`/underwriter`), `business` is the Cova Bots scope, served on the Bots host
 * and never on the platform. It partitions the core
 * engines — mail, calendar, calls, projects, Bots — so one organisation can run
 * more than one product on them. It is a PARTITION, never an authority:
 * `organization_id` stays the tenant and RLS boundary, and grants stay the
 * content authority. Nothing here is nullable and nothing here is a payload
 * field and never a request header: every organisation holds exactly one scope
 * (`organisations.scope`, 022 G48), the identity middleware admits the acting
 * firm's, and every service reads `identity.scope` the way it reads the org.
 *
 * CLOSED IN CODE: a value exists only if a route prefix, an admission rule and
 * an agent product enforce it (the `SubjectKind` rule). Adding a platform is an
 * entry here plus a migration that widens the CHECK constraints; a ratchet
 * asserts the two lists agree.
 *
 * A ROW SAYS WHAT KIND OF PRODUCT IT IS (v1.5.4 README S27, spec 002 H4). It
 * declares its `tenancy` (whose organisation a run belongs to), its `grants`
 * (every one of which must hold) and its `products` family; `admitScope` is
 * DERIVED from that declaration and no row carries a hand-written rule. This is
 * what re-opened S16: "every non-core scope is Cova staff's" was a fact about
 * the one non-core scope that existed, not a property of the primitive.
 * `underwriter` is the first row sold to organisations — caller tenancy, one
 * grant, and no staff capability anywhere near it.
 *
 * Frontend-safe: `effect` and the browser-safe package contracts
 * (`@cova/agent-v2-core/contracts`, `@cova/settings-core/paths`) only.
 */
import { Option, ParseResult, Schema as S } from "effect"
import type { AdmittedScope } from "@cova/agent-v2-core/contracts/authorisation"
import type { OrganizationType } from "@/domain/organisation/organisation"
import {
  AGENT_PRODUCT_ROWS,
  StoredScopeAliasReader,
  familyFor,
  type AgentProductId,
  type AgentProductScopeLiteral,
  type CovaAppLiteral,
} from "@cova/agent-v2-core/contracts/agent-products"
import { SCOPE_SETTINGS_HOMES } from "@cova/settings-core/paths"
import { scopeBillingWall } from "./billing-wall"

/**
 * The closed list, strict: only canonical values. It is the TYPE, the source of
 * `PLATFORM_SCOPES`, and the schema for anything rendered as JSON Schema (tool
 * parameters, OpenAPI): a transformation renders its `from` side, which for
 * the lenient schemas below is `S.String`, so a tool or an OpenAPI field built
 * on one silently loses its enum and the model or client is told any string
 * will do. `stored-scope-alias-json-schemas.test.ts` keeps the lenient names
 * out of tool and HttpApi files.
 */
export const PlatformScopeLiteral = S.Literal("insurance-broker", "underwriter", "business", "staff")
export type PlatformScope = typeof PlatformScopeLiteral.Type

/**
 * THE `/cs` SCOPE (spec docs/specs/multi-product/2026-10-25-002, STAFF-R1,
 * STAFF-FLIP). The firm type the Customer section runs under: the Cova Staff
 * firm's scope. It was stored as `customer` until STAFF-FLIP renamed it in
 * place; a stored `customer` reads as this through `STORED_SCOPE_ALIASES`.
 * Every site that means the scope (not the business word customer) names this
 * constant. R-020-1 (`scope-customer-literal-ratchet.test.ts`) keeps the old
 * literal from coming back.
 */
export const STAFF_SCOPE = "staff" as const satisfies PlatformScope

/**
 * STORED VALUES THAT READ AS A CANONICAL SCOPE (spec
 * TAITRANZ/TODO/DATABASE/2026-10-19-001 D9, D10). The broker product's scope
 * was stored as `core` until the rename to `insurance-broker`. The rename
 * shipped in two PRs: the first taught every reader that `insurance-broker`
 * meant `core`; the switch-over renamed the stored rows and flipped this table,
 * which stays for history: audit hunks, allocation history and stored facts
 * keep `core` permanently, and a tab or a queued job from before the deploy
 * may still send it.
 *
 * The one alias table. `SellingScope`, `BillingScope` and every other
 * schema that closes over the scope read through it; the closed copies in
 * `packages/` (which cannot import this file) are pinned to it by
 * `scope-literal-copies.test.ts` and `agent-product-scope-pin.test.ts`.
 */
export const STORED_SCOPE_ALIASES: Readonly<Record<string, PlatformScope>> = {
  core: "insurance-broker",
  // STAFF-FLIP (spec 025 §3): the `/cs` scope was stored as `customer`. Kept
  // for history after the contract release, exactly like `core`: the ledger
  // and allocation history keep the old spelling forever.
  customer: STAFF_SCOPE,
}

/**
 * Stored values a scope CHECK still admits beside `PLATFORM_SCOPES` (spec 025
 * §3, G4 during the window). v7.11 widens every list CHECK to admit `staff`
 * and keeps `customer` for one promote, so an old pod running during the
 * deploy can still write. Every value here is a `STORED_SCOPE_ALIASES` key, so
 * it decodes; encode never emits one. STAFF-C1 drops `customer` from the
 * CHECKs and empties this list.
 */
export const CONTRACT_PENDING_SCOPE_VALUES: ReadonlyArray<string> = ["customer"]

/**
 * A raw stored or wire value with any alias resolved to its canonical scope;
 * every other value comes back unchanged, so a closed check after it still
 * refuses junk. Own keys only: `"constructor"` is not an alias.
 */
export const canonicalScopeValue = (raw: string): string =>
  Object.hasOwn(STORED_SCOPE_ALIASES, raw) ? (STORED_SCOPE_ALIASES[raw] ?? raw) : raw

/**
 * Every stored spelling of a scope: its canonical value plus each alias that
 * maps to it, canonical first. An unknown or open value is only itself.
 *
 * FOR HISTORY READERS ONLY (spec TAITRANZ/TODO/DATABASE/2026-10-19-001 D15).
 * A live table holds only the canonical value after the v6.20 rename (its
 * BEFORE triggers rewrite a late alias write), so a live-table predicate binds
 * the scope as a scalar, `col = ${scope}`, which the scope indexes serve; an
 * array bind moved five calls plans off them. Only a table that still holds an
 * alias, append-only history like `agent_model_allocation_history`, may match
 * `col = ANY(${scopeSpellingsOf(x)}::text[])`, and each such statement is on
 * the scoped-list ratchet's D15 allowlist with its reason.
 */
export const scopeSpellingsOf = (scope: string): ReadonlyArray<string> => {
  const canonical = canonicalScopeValue(scope)
  return [
    canonical,
    ...Object.keys(STORED_SCOPE_ALIASES).filter((alias) => alias !== canonical && STORED_SCOPE_ALIASES[alias] === canonical),
  ]
}

/**
 * A closed scope schema that READS aliases: decodes any `STORED_SCOPE_ALIASES`
 * key to its canonical value, then validates against `canonical`; encodes only
 * canonical values. `S.is` on the result checks the Type side, so it stays
 * strict. Every lenient scope-shaped schema is built here, so the alias table
 * is never restated, and each carries the `StoredScopeAliasReader` annotation.
 */
export const readingStoredScopeAliases = <A extends string, I>(canonical: S.Schema<A, I>): S.Schema<A, string> =>
  S.transformOrFail(S.String, S.typeSchema(canonical), {
    strict: true,
    decode: (raw) => ParseResult.decodeUnknown(canonical)(canonicalScopeValue(raw)),
    encode: (value) => ParseResult.succeed(value),
  }).annotations({ [StoredScopeAliasReader]: true })

/**
 * The scope schema every row, RPC field, stored fact and event payload decodes
 * with. LENIENT on decode (an alias reads as its canonical value), strict on
 * encode. Use `PlatformScopeLiteral` where a schema is rendered as JSON Schema.
 */
export const PlatformScope: S.Schema<PlatformScope, string> = readingStoredScopeAliases(PlatformScopeLiteral)

export const PLATFORM_SCOPES: ReadonlyArray<PlatformScope> = PlatformScopeLiteral.literals
export const DEFAULT_PLATFORM_SCOPE: PlatformScope = "insurance-broker"

/** Strict: for values already decoded. A raw string goes through `normalisePlatformScope`. */
export const isPlatformScope = S.is(PlatformScopeLiteral)
export const decodePlatformScope = S.decodeUnknownOption(PlatformScope)

/**
 * RAW input (a column read as `string`, a query param, a saved filter, a
 * header) to a scope, reading aliases. `None` for anything else.
 */
export const normalisePlatformScope = (raw: unknown): Option.Option<PlatformScope> => decodePlatformScope(raw)

/** Raw scope strings (holdings, a filter list) to the known scopes among them, aliases read, order kept. */
export const normalisePlatformScopes = (raw: ReadonlyArray<string>): ReadonlyArray<PlatformScope> =>
  raw.flatMap((value) => Option.toArray(normalisePlatformScope(value)))

/**
 * An OPEN scope value (holdings, `heldProducts`, `accessibleScopes`): read
 * open on purpose so a value a newer deploy wrote cannot fail the decode
 * (002 H8), but with a stored alias resolved, so a holding stored as an alias
 * still answers `includes(canonical)`. Unknown values pass unchanged.
 */
export const OpenScopeValue: S.Schema<string> = S.transform(S.String, S.String, {
  strict: true,
  decode: canonicalScopeValue,
  encode: (value) => value,
}).annotations({ [StoredScopeAliasReader]: true })

/**
 * Per-scope counts (a facet, a stat card) with every row folded into its
 * canonical scope and the counts summed, first-seen order kept, so a stored
 * alias never shows as a facet of its own beside its canonical scope.
 */
export const foldScopeCounts = (
  rows: ReadonlyArray<{ readonly scope: string; readonly count: number }>,
): ReadonlyArray<{ readonly scope: string; readonly count: number }> => {
  const totals = new Map<string, number>()
  for (const row of rows) {
    const scope = canonicalScopeValue(row.scope)
    totals.set(scope, (totals.get(scope) ?? 0) + row.count)
  }
  return [...totals].map(([scope, count]) => ({ scope, count }))
}

/**
 * The identity fields an admission rule may read — a structural subset of
 * `AuthorisedIdentity` so this module never imports the identity contract.
 */
export interface ScopeAdmissionIdentity {
  readonly orgId: string
  /**
   * The PRODUCTS the resolved organisation actively holds — the `scope` column
   * of its live `organisation_products` rows (spec v1.5.4/002 H8, S31).
   *
   * REQUIRED, and never defaulted at a call site. Both defaults are a bug with
   * a direction: `[]` locks every existing user out of the product they are
   * standing in, and `["insurance-broker"]` admits an organisation to a product nobody
   * granted it. A construction site that cannot answer this has not resolved an
   * identity yet.
   */
  readonly heldProducts: ReadonlyArray<string>
  readonly capabilities?: ReadonlyArray<string>
  readonly platformOrgId?: string | null
  readonly isSuperAdmin?: boolean
}

/**
 * Whose organisation a run in this scope belongs to (S27).
 *   "caller"   — the caller's own organisation bought the product.
 *   "platform" — Cova's own organisation is the tenant; the caller is staff.
 */
export type PlatformScopeTenancy = "caller" | "platform"

/**
 * One thing that must be true for an identity to enter a scope. A row lists
 * them; ALL of them must hold.
 *
 * Declaring the grants is what removed the special case: `customer` used to
 * carry a hand-written predicate, which meant the rule lived per row and
 * nothing could assert across rows. Now the row says what kind of product it
 * is and `admitScope` is derived from that (002 §4.4, README S27).
 */
export type PlatformScopeGrant =
  /** The organisation holds this row's product (`organisation_products`). */
  | { readonly kind: "holdsProduct" }
  /**
   * The PERSON holds a Cova staff capability under this prefix, or the
   * wildcard. `prefix: null` is ANY live staff capability (D72, 022 G49 (1)):
   * the staff scope admits every staffer, and each section or agent product
   * then checks its own key (`/cs` needs `cs.*`, curation and enrichment theirs).
   */
  | { readonly kind: "staffCapability"; readonly prefix: string | null }

/**
 * THE PRODUCT MANIFEST — everything shared code needs to know about a product
 * (spec customer-success/v1.5.4/004 K1).
 *
 * ═══ WHY THE ROW AND NOT A CHECKLIST ═══
 *
 * Before 004 the facts about a product were spread across the files that
 * happened to need them, written as literals: exclusivity was a pair of
 * branches in `grantRefusalFor`, the catalogue ladder was a ternary in
 * `feature-catalogue.ts`, the billing wall was "any non-core prefix is exempt"
 * in `BillingGate`, and the home page was a constant in the product's own shell.
 * None of that is wrong for two products; all of it is a place the THIRD one
 * silently half-works, because nothing anywhere fails when a fact is missing.
 *
 * So the row carries them, and the rules read the row. The test is §0 of the
 * spec: adding a product is a row, a ladder, a layout, an agent config and a
 * CHECK-widen release — and anything else that needs to know is either derived
 * from here or a red test until it is.
 *
 * ═══ GENERIC OVER THE SCOPE KEY, AND WHY THAT IS NOT ABSTRACTION FOR ITS OWN
 * SAKE ═══
 *
 * `S` is the scope literal union. Production always instantiates it at
 * `PlatformScope`, which `ScopeManifest` defaults to. It is a parameter so
 * that a TEST can build a registry with a FOURTH row — a product that does not exist — and run every
 * derivation over it without widening `PlatformScope`, which would make the
 * synthetic product admissible in production. A rule that needs an `if` for
 * product #4 fails there; that harness is the proof §0 asks for.
 */
/**
 * WHICH PLAN LADDER A PRODUCT'S REFUSALS MAY NAME (K6 follow-up (b)). No row
 * declares it (018 §2.2, R-018-8): `planLadderOf` derives it from the row's
 * `planPort` and `catalogueScope`.
 *
 * `"cova-tiers"` — the broker's base/pro/max seat ladder. A refusal may name
 * the tier that grants the feature and offer the seat-upgrade CTA.
 *
 * `"underwriter-tiers"` / `"business-tiers"` — a Teams scope's own ladder
 * (`<scope>-tiers`). A refusal may name its rungs and never a Cova tier or
 * core route.
 *
 * `"none"` — the product has no ladder. A refusal must name NO tier, offer no
 * "Upgrade", and never link to `/settings/billing`. It says the capability is
 * not enabled for the ORGANISATION and who can change that.
 *
 * Not the wall: `customer` walls nothing and yet `/cs` reads Cova's ladder
 * (S33), because it bills through the broker catalogue.
 */
export type PlanLadder = "cova-tiers" | "underwriter-tiers" | "business-tiers" | "none"

/** The ladder name a plan port gives scope `S` (`teams` → the scope's own `<scope>-tiers`). */
type LadderOfPort<S extends string, P> = P extends "insurance-broker"
  ? "cova-tiers"
  : P extends "teams"
    ? `${S}-tiers` extends PlanLadder
      ? `${S}-tiers`
      : never
    : "none"

/**
 * WHICH OF A PRODUCT'S SURFACES THE BILLING WALL MAY COVER (UW-L9). DERIVED
 * from the row's billing facts by `billingWallPolicyOf` (023 G57 (3), see
 * `billing-wall.ts`); no row declares it.
 *
 * `"all-surfaces"` — every surface of the product walls: the broker product's
 * shape, and what `billingWall: true` meant.
 * `"none"` — no surface ever walls: `customer` (RUNBOOK D19), and what
 * `billingWall: false` meant.
 * `"paid-rungs"` — only the surfaces whose entitlement needs a rung ABOVE the
 * ladder's floor wall, and they are named by `paidRungPaths`. Every other
 * surface of the product is a floor surface and is never walled (DF4), which
 * is what lets an admin of a past-due organisation always reach billing.
 */
export type BillingWallPolicy = "none" | "all-surfaces" | "paid-rungs"

/** Every policy, for the ratchets that assert a row answers with one of them. */
export const BILLING_WALL_POLICIES: ReadonlyArray<BillingWallPolicy> = [
  "none",
  "all-surfaces",
  "paid-rungs",
]

/**
 * WHICH BILLING RAIL A PRODUCT IS SOLD ON (uplift-01 spec 002 §2.1, T0).
 *
 * `"broker-cascade"` — the broker product's Stripe cascade: seat vectors,
 * split tiers, networks, custom pricing.
 * `"product"` — the product's own subscription rail (`underwriter`).
 * `"none"` — never billed on its own; billed through another product's account
 * (`customer`).
 *
 * Only `billingRail` decides billing UI and behaviour, never `planLadder`. This
 * union and `BILLING_RAILS` are the only places the literals are written.
 */
export const BILLING_RAILS = ["broker-cascade", "product", "none"] as const
export const BillingRail = S.Literal(...BILLING_RAILS)
export type BillingRail = typeof BillingRail.Type

/**
 * WHICH KIND OF PLAN A SCOPE IS SOLD ON (spec multi-product 018 §2.2, 022
 * G50 (4)). Ports are kinds of plan, not scopes:
 *
 * `"insurance-broker"` — the broker's plan: today's cascade, `COVA_TIERS`.
 * `"teams"`: a Teams-port plan, the scope's own `<scope>_<rung>` ladder,
 * with the row's `floor` first. Business and underwriter both sell it; there
 * is no `underwriter` port.
 * `"none"` — never billed (customer, staff).
 *
 * `ladderFor(scope)` is derived from this field and `floor`. These are the
 * only places the literals are written; `@cova/settings-core`'s
 * `PLAN_PORT_KINDS` is the package's copy (a package cannot import this file).
 */
export const PLAN_PORTS = ["insurance-broker", "teams", "none"] as const
export const PlanPortKey = S.Literal(...PLAN_PORTS)
export type PlanPortKey = typeof PlanPortKey.Type

/**
 * THE PLAN PORT EACH BILLING RAIL BECOMES (018 G39 (6), §5.2, §10): a rename
 * of the key only. `broker-cascade` is the `insurance-broker` port, `product`
 * the `teams` port. The operator port registry and the console rail modules
 * are keyed by plan port; a summary, a stored write mode or a console list row
 * still carries the rail key on the wire (expand), so readers map it here.
 * CONTRACT: once every wire field and stored key carries a plan-port key,
 * `BillingRail`, this map and `planPortOfRail` are deleted.
 */
export const PLAN_PORT_OF_RAIL = {
  "broker-cascade": "insurance-broker",
  product: "teams",
  none: "none",
} as const satisfies Readonly<Record<BillingRail, PlanPortKey>>

/** The rail key each plan port still answers to on the wire (the inverse of `PLAN_PORT_OF_RAIL`). */
export type RailOfPlanPort = { readonly [R in BillingRail as (typeof PLAN_PORT_OF_RAIL)[R]]: R }

export const RAIL_OF_PLAN_PORT: RailOfPlanPort = {
  "insurance-broker": "broker-cascade",
  teams: "product",
  none: "none",
}

/** The plan port behind a rail key; a plan-port key passes through, so old and new wire keys both resolve. */
export const planPortOfRail = (key: BillingRail | PlanPortKey): PlanPortKey =>
  Object.hasOwn(PLAN_PORT_OF_RAIL, key) ? PLAN_PORT_OF_RAIL[key as BillingRail] : (key as PlanPortKey)

/**
 * THE SEATLESS TIER A FIRM FALLS TO with no seats (018 §2.2, 023 G60 (2)):
 * underwriter's Free. `key` is the stored tier, `label` its one display name.
 * What the floor grants is only its capability row, e.g. `(underwriter, free)`.
 * Only a `teams` row may declare one; when its subscription ends a firm with a
 * floor returns to it, and a firm without one locks out.
 */
export interface PlanFloor {
  readonly key: string
  readonly label: string
}

export interface ScopeManifest<S extends string = PlatformScope> {
  readonly scope: S
  /** UI noun. */
  readonly label: string
  /** The app route prefix whose client layers send the header. `null` for core. */
  readonly routePrefix: string | null
  /**
   * Further route prefixes whose pages are this scope's own sections, beside
   * `routePrefix` (STAFF-G5b, 020 G44 (3)): the staff row's `/super-admin`, the
   * staff shell's second section. `scopeForPath` reads them as it reads
   * `routePrefix`, so a tab on `/super-admin` claims its STAFF firm in
   * `x-cova-org` (the per-tab active-firm store, D49) and never the broker
   * firm it holds beside it. Not a home and not a header prefix of its own:
   * every link into the scope is still built from `routePrefix`. Omitted by
   * every row with one section.
   */
  readonly sectionPrefixes?: ReadonlyArray<string>
  /**
   * Where an admitted person LANDS, and where another surface sends someone who
   * holds this product. Not the same as `routePrefix`: `/underwriter`'s home is
   * `/underwriter/profile`, and `insurance-broker` has no prefix at all but does have a home.
   *
   * A ratchet asserts `scopeForPath(homePath) === scope` for a row with a
   * prefix — a home that resolves to another product's scope is a redirect
   * loop, which is precisely what R-1 and R-2 were.
   */
  readonly homePath: string
  /**
   * The app this scope's firms work in (platform model, ruled 2026-10-27):
   * `bots` for `business`, `platform` for the other three. A link built for a
   * firm outside any app (a notification's digest line or Slack message) goes
   * on this app's origin, so a business firm's link opens Cova Bots even for
   * a person whose other firm is a broker on the platform. `homePath` is a
   * path on this app's host only where the app is `platform`; on another app
   * the firm's home is that app's own (`APP_HOMES` in `@cova/shell/home`).
   */
  readonly app: CovaAppLiteral
  /**
   * Whether the product is offered to people: a pill in the product switcher,
   * and its rail row. `false` while it is built: only super admins are offered
   * it, and everyone else comes across it nowhere (Tai, 2026-10-21). Admission
   * is not this: a holder of an unlaunched product is still admitted at its URL.
   */
  readonly launched: boolean
  /**
   * Where this product's OWN settings live (spec v1.5.4 G3).
   *
   * A product carries its own settings; core `/settings` is not made
   * scope-aware. So a person who holds only this product and types the CORE
   * address is sent HERE, and the redirect site derives it from the row rather
   * than naming a product — a fourth product needs no edit there.
   *
   * Distinct from `homePath`: the home is where an admitted person LANDS, the
   * settings path is one destination inside the product. `/underwriter` lands on
   * the profile and keeps its settings at `/underwriter/settings`.
   *
   * The registry does not declare the path: a platform-hosted scope's row
   * reads `SCOPE_SETTINGS_HOMES[scope].settings` from the one section-path
   * table (`@cova/settings-core/paths`, R-021-5, 021 S5), and so does
   * `inboxSettingsPath` below. A scope with no settings pages of its own
   * points both at its home.
   */
  readonly settingsPath: string
  /**
   * WHERE A STUCK PERSON READS ABOUT THIS PRODUCT (P-23).
   *
   * `null` means "this product has no help destination yet", which today is
   * every row: the audit looked for a docs or support URL anywhere in `src` and
   * found none, for `insurance-broker` either. So the refusals name a human — "your Cova
   * account manager", "check with Cova support" — with nothing clickable beside
   * them, and a person who cannot get in has nowhere to go.
   *
   * The FIELD lands now and the VALUE is an owner decision: a URL invented here
   * would ship a link that 404s, which is worse than the gap. Every surface that
   * renders it reads this accessor and renders nothing while it is `null`, so
   * the day the owner fills one row in, that product's help link appears with no
   * further change — and a fourth product gets one by declaring it.
   */
  readonly helpUrl: string | null
  /**
   * WHO A STUCK PERSON WRITES TO (P-22).
   *
   * `null` means "this product has no support address of its own", and the
   * surfaces fall back to the platform's through `scopeSupportEmail` below.
   *
   * The audit found the Underwriter product's own screens — self-signup, claim,
   * claim recovery — naming Cova-the-broker's address in seven places, once
   * misspelled. The misspelling was fixed on the spot; the ADDRESS being the
   * broker product's is the part that needs a decision, because an underwriter
   * writing to it reaches the team that supports somebody else's product.
   *
   * So the field lands and the value is an owner call, exactly as `helpUrl` is.
   * Until one is supplied nothing changes on screen: the fallback is the
   * address those screens carry today, declared once here instead of seven
   * times in JSX.
   */
  readonly supportEmail: string | null
  /**
   * Where a COMPLETED mailbox connect lands for this product (004 audit A18).
   *
   * A field, because the callback site used to build
   * `` `${routePrefix}/settings/inboxes` `` — reading one manifest value and
   * then assuming the shape of the rest of the tree, which reads like
   * derivation and is not. The cost of guessing wrong is paid at the worst
   * moment: the redirect happens AFTER the connection row is written, so the
   * mailbox is connected and the broker is looking at a 404 that tells them it
   * failed. `insurance-broker` is the proof that the guess was a guess — its page is
   * `/settings/accounts`, which no concatenation of its row produces.
   *
   * Not derivable from `settingsPath` either: "settings plus one segment" is
   * the same assumption one level down.
   */
  readonly inboxSettingsPath: string
  /**
   * What a sentence calls that page, as this product's screens title it
   * (P32): the agent's connect and reconnect remedies say "in <label>". `null`
   * where the product has no page of its own, its `inboxSettingsPath` being
   * its home.
   */
  readonly inboxSettingsLabel: string | null
  /**
   * WHERE THIS PRODUCT'S CALENDAR WORKSPACE LIVES (004 audit A18, client half).
   *
   * `calendar-scope-routes.ts` built this as `` `${routePrefix}${corePath}` ``
   * — the same "read one manifest value and assume the shape of the rest of
   * the tree" that `inboxSettingsPath` exists to stop. It is right for all
   * three rows today, which is exactly why it needed a field before a fourth.
   */
  readonly calendarPath: string
  /**
   * WHERE THIS PRODUCT'S BOOKING-LINKS WORKSPACE LIVES (004 audit A18).
   *
   * ═══ WHY THIS IS A SECOND FIELD AND NOT `calendarPath + "/booking"` ═══
   *
   * Because that is FALSE TODAY, and provably:
   *
   *   core        `/calendar`             `/calendar/booking`
   *   customer    `/cs/calendar`          `/cs/calendar/booking`
   *   underwriter `/underwriter/calendar` `/underwriter/booking`   ← not nested
   *
   * `/underwriter/calendar/booking` does not exist (`src/app/underwriter/(app)/`
   * has `booking/` and `calendar/` as siblings), so the concatenation sends an
   * underwriter clicking "Booking links" inside their own calendar to a 404 —
   * live, today. One field cannot express the pair: whatever rule derived
   * underwriter's booking path from its calendar path would give core and
   * customer the wrong answer, and vice versa. Two facts, two fields.
   */
  readonly bookingPath: string
  /**
   * WHERE A TEAM INVITATION INTO THIS PRODUCT'S ORGANISATION LANDS (005 E4b,
   * UW-M11/UW-S9).
   *
   * The Clerk invitation's `redirectUrl` is where the invitee finishes signing
   * up. `null` means "the platform's own accept-invitation page", which is the
   * broker flow and every row that lives inside it (`customer` is held beside
   * `insurance-broker`, so its organisations are brokerages). A product with its own signup
   * surface names it here, and the invite service reads it off the product the
   * organisation HOLDS rather than naming a product.
   *
   * A field and not `routePrefix` plus a suffix: the Underwriter ticket page
   * sits OUTSIDE its prefix (`/invitation/…`, next to the claim flow that
   * shares it), and `/underwriter/signup` is the self-serve form, which does
   * not redeem a Clerk ticket. The concatenation would send the invitee to a
   * page that cannot accept the invitation.
   */
  readonly invitationSignupPath: string | null
  /**
   * WHERE THIS PRODUCT MOUNTS THE FORMS ENGINE, or `null` when it does not
   * (forms v1.5.0 spec 004 §7, 004-LINK-1).
   *
   * A field and not `routePrefix + "/forms"`, for the reason `bookingPath` is
   * one (audit A18): the concatenation reads like derivation and is a guess
   * about the shape of the rest of the tree. Every forms destination is built
   * from this value by `formsRoutes(scope)` in the forms core leaf; no call
   * site writes a forms path of its own.
   *
   * `null` is load-bearing: a product that does not mount forms has no forms
   * link, and asking it for one is a server-side defect
   * (`FormsNotMountedError`), never a silent fall back to core's `/forms`.
   */
  readonly formsPath: string | null
  /** See `PlatformScopeTenancy`. Asserted against the agent product policy by a ratchet. */
  readonly tenancy: PlatformScopeTenancy
  /** Every grant must hold. Customer needs both of today's two kinds. */
  readonly grants: readonly [PlatformScopeGrant, ...PlatformScopeGrant[]]
  /**
   * The agent products that run in this scope — the scope's FAMILY, with the
   * canonical one first (`agentProductForScope` is `products[0]`).
   *
   * A tuple rather than one string because a scope is a product surface and a
   * product surface can host more than one agent: `insurance-broker` hosts six. Read it
   * through the accessors below, never as a field — the accessor seal
   * (`registry-product-accessor-ratchet.test.ts`) is what keeps the next change
   * to this relation inside this file (S30).
   */
  readonly products: readonly [string, ...string[]]
  /**
   * WHICH CATALOGUE LADDER this product is measured against (lock S33).
   *
   * Usually itself. `customer` is the exception and the reason this is a field
   * rather than an identity: `/cs` is a STAFF surface over the broker product —
   * a Cova staffer working a brokerage's board is using Cova's own tools, not a
   * product their organisation bought — so it reads `insurance-broker`'s ladder. Resolving
   * it against a ladder of its own would plan-lock every `/cs` room, which is a
   * defect and not a tier ("everyone is Max" in `/cs`).
   *
   * `SellingScope` is DERIVED from this field: the products that sell
   * are the ones whose ladder is their own. That is what keeps `soldBy: {
   * customer: … }` a compile error without a second list to maintain.
   */
  readonly catalogueScope: S
  /**
   * Products that may NOT be held alongside this one (O10 as data: `insurance-broker` ↔
   * `underwriter` in v1; the table permits both, the service refuses).
   *
   * MUST be symmetric, and a ratchet asserts it: a one-sided entry is a grant
   * path that refuses in one direction and admits in the other, which is how an
   * organisation ends up holding a pair the product says it cannot.
   */
  readonly exclusiveWith: ReadonlyArray<S>
  /**
   * The route prefixes of this product's PAID-RUNG surfaces — the ones whose
   * entitlement needs a rung above the ladder's floor (UW-L9).
   *
   * There is no `billingWall` field (023 G57 (3), WP3.13.9a): the wall is
   * derived from `planPort`, `floor` and `catalogueScope` by
   * `billingWallPolicyOf`, so a paid product #4 is walled by the plan it is
   * sold on, never by a second declaration that could disagree with it.
   *
   * Read only under a `"paid-rungs"` wall, and a ratchet holds both
   * directions: a `paid-rungs` row names at least one path, every other row
   * names none (a list the wall never reads is a list nobody notices is wrong).
   *
   * An ALLOW-list of what walls, not a list of what is exempt: a surface this
   * list forgets is a floor surface and stays open, which is the failure DF4
   * can live with. The reverse — a new free page walled by default — is the
   * failure that locks an admin out of the page they would pay from.
   */
  readonly paidRungPaths: ReadonlyArray<string>
  /**
   * How this product's money moves (uplift-01 002 §2.1, RF-1). NOT the ladder:
   * `customer` reads `cova-tiers` and is billed nowhere of its own.
   *
   * `"broker-cascade"` — pricing_scopes cascade, seat groups, add-ons, networks,
   * pins (the default product only).
   * `"product"` — `resolveForProduct`, a `product_plan_key` ladder, the trials
   * ledger.
   * `"none"` — not billed on its own account (bills as another product, or is
   * not sold).
   *
   * Only this field decides billing UI and behaviour, never `planLadder`.
   * `billing-rail-coherence.test.ts` holds the rules that tie it to the other
   * facts (C1–C4, C-plan).
   */
  readonly billingRail: BillingRail
  /**
   * The kind of plan this scope is sold on (018 §2.2). Replaces `billingRail`
   * once the plan-ports leaf reads it; until then both are declared and
   * `plan-ladder-teams.test.ts` ties them together. See `PlanPortKey`.
   */
  readonly planPort: PlanPortKey
  /**
   * The seatless tier a firm on this scope falls to (`PlanFloor`), or `null`
   * for a scope with none. First on `ladderFor(scope)`.
   */
  readonly floor: PlanFloor | null
  /**
   * Where the product's own admin chooses and pays for a plan, RELATIVE to the
   * row's `routePrefix` (underwriter: `/welcome/plan`). `null` exactly when the
   * rail is not `"product"` (rule C-plan). The product rail adapter builds the
   * staff "send plan link" URL from it, so no adapter names a product.
   */
  readonly planPagePath: string | null
  /**
   * Where the product's Embedded Checkout returns to, RELATIVE to the row's
   * `routePrefix` (underwriter: `/welcome/return`). `null` exactly when
   * `planPagePath` is null (rule C-return, spec underwriter/2026-10-17-001
   * D10). The join gate never walls it (R7 AC3): an admin coming back from a
   * paid checkout must reach the page that confirms it.
   */
  readonly checkoutReturnPath: string | null
  /**
   * The trial this product sells, by POLICY ID, or `null` for a product with no
   * declared trial (UW-T1, spec underwriters/product-v1/003 §2.1).
   *
   * An id and not the policy itself: the policies are billing's declarations
   * (`src/contexts/billing/core/domain/trial/trial-policy.ts`), which import this
   * file, so the row names one and a ratchet there proves every named id is a
   * policy for THIS row's catalogue product on a rung of THIS row's ladder.
   * A new policy version is a new id, so bumping it is a one-line row edit.
   *
   * `null` for the broker row on purpose: its trial is the super-admin trial,
   * not yet expressed as a policy (003 UW-T14 proves it can be, without
   * switching it).
   */
  readonly trialPolicy: string | null
  /**
   * Which organisation type is given this product at creation, or `null` for a
   * product nobody is provisioned into (Customer is assigned, never created).
   *
   * The authority at runtime is the v5.42 `organisations_default_product`
   * trigger — SQL cannot read this file, and an applied migration is never
   * edited. So this is the DECLARATION the trigger is checked against:
   * `__tests__/default-holding-trigger-ratchet.test.ts` reads the LATEST
   * included release that defines the function, parses its `CASE`, and asserts
   * set-equality with these rows. That turns "a fourth product whose trigger arm
   * was forgotten" into a red test rather than a silent lockout where every new
   * organisation of that type holds the WRONG product.
   *
   * (That ratchet was claimed here from 002 and did not exist until the
   * architecture audit found the gap — A6. The claim was load-bearing: it is
   * the stated reason this field is safe to declare in TypeScript at all.)
   *
   * A product that names a NEW organisation type owes THREE things in the same
   * change: this row, a forward migration redefining the trigger function, and
   * the `organisations_organization_type_check` widen — see the recipe's §2.5.
   * Retired with `organization_type` itself (E1).
   *
   * TYPED AS THE LITERALS, not `string` (004 audit A7). A typo here used to be
   * a runtime mystery — every new organisation of that type silently holds the
   * wrong product — and is now a compile error. The domain `OrganizationType`
   * is imported TYPE-ONLY: its module reaches `@effect/sql` through `Model`,
   * but a type import is erased, so this file stays frontend-safe without
   * restating the union (a hand copy went stale when v6.38 added `business`).
   * `__tests__/organization-type-closure-ratchet.test.ts` asserts the rows,
   * the `OrganizationType` schema and the `organisations_organization_type_check`
   * CHECK agree, so widening the union without the CHECK is red.
   */
  readonly defaultFor: { readonly organizationType: OrganizationType } | null
  /**
   * May a PERSONAL mailbox (`provider_connections.owner_type = 'user'`) be
   * exposed to this scope?
   *
   * ═══ WHY THIS IS A SCOPE PROPERTY AND NOT AN OWNERSHIP TEST ═══
   *
   * It began as a hard-coded `ownerType === 'user'` refusal in
   * `ProviderConnectionService.setConnectionScopes` (v1.5.1/005 §4.9, decision
   * V1/S17 = forbid). Written that way the rule reads as "personal mailboxes
   * are special", which is not what anyone decided — every scope would inherit
   * the ban, including one added years from now for reasons nobody has thought
   * of yet. The real question is about the SCOPE: what does being exposed to
   * this product mean for a mailbox, and is that something an owner can
   * meaningfully agree to? So a new platform has to answer it, here, once.
   *
   * ═══ WHY `customer` ANSWERS YES (2026-09-16) ═══
   *
   * S17's stated reason was that `historyVisibility: "shared-super-admin"`
   * makes a dual-exposed mailbox "a TEAM corpus — anyone holding the other
   * product's agent capability can search it". Verified against the kernel, the
   * first half is not true: `connection-access-queries.ts` admits a connection
   * on the scope arm AND (`owner_type='user' AND owner_user_id = actor` OR a
   * live `connection_grants` row). Holding `cs.agent` satisfies neither half,
   * so a colleague can no more read a personal `customer` mailbox than a
   * personal `insurance-broker` one. Every agent tool resolves its actor from the REQUEST
   * identity (`ctx.userId`), not the session owner, so a staffer opening a
   * colleague's shared session still reads only their own mail.
   *
   * What IS shared is the TRANSCRIPT: text a colleague's run already rendered.
   * That is the product working as designed — Customer history is shared so a
   * thread can be picked up — and the honest answer to "nobody clicking a chip
   * is agreeing to that" is to SAY SO at the chip, which the settings card now
   * does, not to refuse the capability. CS staff work support and sales mail
   * out of their own inboxes; that is the job, not a leak.
   *
   * Withdrawing a scope is unconditional and is not governed by this flag: a
   * person must always be able to pull their mailbox back out.
   */
  readonly admitsPersonalConnections: boolean
  /**
   * Where this product records a CLAIM — an organisation taking ownership of a
   * listing — or `null` for a product nobody claims into (RUNBOOK D45, AUDIT-2
   * W2). The floor-rung conversion job reads it to find CLAIMED organisations
   * that hold no active grant of the product (`NO_HOLDING`); a `null` row has
   * no claims, so no such check.
   *
   * A declaration of the claim table's SHAPE, not a query: the SQL lives in
   * billing's product-axis repository (`readClaimedWithoutHolding`), which
   * reads these names as quoted identifiers. Claimed = `statusColumn =
   * claimedStatus` with `orgColumn` set; the organisation's region is read off
   * `organisations`, never the claim table.
   */
  readonly claimSource: ClaimSource | null
}

/** The claim table a product declares (see `ScopeManifest.claimSource`). */
export interface ClaimSource {
  /** The table holding one row per claimable listing. */
  readonly table: string
  /** The column naming the claiming organisation (`organisations.id`). */
  readonly orgColumn: string
  /** The column carrying the claim state. */
  readonly statusColumn: string
  /** The `statusColumn` value that means claimed. */
  readonly claimedStatus: string
}

/** A registry of manifests keyed by scope — the real one, or a test's. */
export type ScopeRegistry<S extends string> = Readonly<Record<S, ScopeManifest<S>>>

/**
 * Does the person hold a staff capability under `prefix` (or the wildcard)?
 * `null` asks for ANY staff capability. `identity.capabilities` carries staff
 * keys only (the grants on the person's staff-firm membership, 022 G49 (1)).
 * Exported so the shared surface gate asks the same question (one predicate).
 */
export const hasStaffCapability = (
  identity: Pick<ScopeAdmissionIdentity, "capabilities" | "isSuperAdmin">,
  prefix: string | null,
): boolean =>
  identity.isSuperAdmin === true ||
  (identity.capabilities ?? []).some((key) => key === "platform.all" || prefix === null || key.startsWith(prefix))

/**
 * The capability prefix the `/cs` SECTION needs, on top of admission to the
 * staff scope (D72): the scope admits any staffer, the section a `cs.*` holder.
 */
export const CS_SECTION_CAPABILITY_PREFIX = "cs."

/**
 * THE STAFF SECTION ACCESS PREDICATE (U15, STAFF-G5): the acting firm's scope
 * is `staff` (every grant of the staff row holds) AND the person holds a
 * `cs.*` capability or the wildcard on that membership. It replaced
 * `hasCustomerAccess`, which asked the capability alone and left the org
 * question to a platform-org equality that no longer exists (022 G49 (2)).
 * Admission to the staff SCOPE is wider (any staff capability, D72), so a
 * curator is admitted to the scope and still refused `/cs` here.
 *
 * EXPORTED so there is exactly one copy (v1.5.4/001 P16). `/cs`'s client gate
 * (`src/features/customer/section/lib/section-gate.ts`) delegates here, so the
 * browser cannot render a section the server would refuse.
 *
 * The parameter is the SUBSET this predicate reads, not the whole admission
 * identity: no org id, because no grant of the row reads one. That is what lets
 * the browser's `Identity` (whose `orgId` is nullable before a membership
 * resolves) delegate without inventing an org id it does not have.
 *
 * The scope's grants are read off the row, never restated, so the row stays
 * the one place the staff admission is written.
 */
export const staffSectionAccess = (
  identity: Pick<ScopeAdmissionIdentity, "capabilities" | "isSuperAdmin"> & {
    /** The acting firm's one scope (`organisations.scope`, 022 G48), or null. */
    readonly firmScope: string | null
  },
): boolean =>
  PLATFORM_SCOPE_REGISTRY[STAFF_SCOPE].grants.every((grant) =>
    grant.kind === "holdsProduct" ? identity.firmScope === STAFF_SCOPE : hasStaffCapability(identity, grant.prefix),
  ) && hasStaffCapability(identity, CS_SECTION_CAPABILITY_PREFIX)

/**
 * ═══ `as const satisfies`, NOT A TYPE ANNOTATION (004 K1) ═══
 *
 * An annotation (`: Readonly<Record<…>>`) widens every field to its declared
 * type, so `typeof PLATFORM_SCOPE_REGISTRY.staff.catalogueScope` would be
 * `PlatformScope` and NOTHING could be derived from the rows. `satisfies` keeps
 * the literal types while still checking the shape, which is what lets
 * `SellingScope` below be computed from the rows instead of being a
 * second hand-written list that has to be pinned against them.
 */
/**
 * NO HELP DESTINATION EXISTS YET — an OWNER ITEM, not a default (P-23).
 *
 * Deliberately a named constant rather than a bare `null` in three rows: a
 * `null` reads as "this product deliberately has no help page", and the truth is
 * that nobody has been given the URL. Replace it per row with the real address
 * when there is one; the render sites need no edit.
 */
export const HELP_URL_NOT_YET_SUPPLIED: string | null = null

/**
 * The platform's support address — Cova's own, and the fallback every product
 * without one of its own resolves to.
 *
 * ONE SPELLING, ONE PLACE. The tree carries three (`support@cova.com.au` in
 * eight files, `support@cova.au` in six, `support@cova.ai` in the customer-success
 * intake), and which is real is an open owner item. This constant does not
 * settle that: it is the value the surfaces this field wires already show,
 * moved out of their JSX so the answer, when it comes, is one edit.
 */
export const PLATFORM_SUPPORT_EMAIL = "support@cova.com.au"

/**
 * NO SUPPORT ADDRESS OF ITS OWN — an OWNER ITEM, not a default (P-22).
 *
 * Named rather than a bare `null` for the same reason `HELP_URL_NOT_YET_SUPPLIED`
 * is: `null` reads as "this product deliberately routes to the platform", and
 * the truth is that nobody has been given the product's address.
 */
export const SUPPORT_EMAIL_NOT_YET_SUPPLIED: string | null = null

/**
 * A scope's agent product FAMILY, built from the product rows whose scope it
 * is, canonical first (spec agent-v2/2026-10-13-003 §5.7). The rows live in
 * `@cova/agent-v2-core`, which this domain file may import; a product joins a
 * family by declaring its row, never by an edit here. Built inside this file
 * so the accessor seal (`registry-product-accessor-ratchet.test.ts`) holds.
 */
const familyOf = (scope: AgentProductScopeLiteral): readonly [AgentProductId, ...AgentProductId[]] => {
  const rows = AGENT_PRODUCT_ROWS.filter((row) => row.scope === scope)
  // `canonical` is per (scope, app): the family's head is the platform's.
  const isHead = (row: (typeof rows)[number]): boolean => row.canonical && row.app === "platform"
  const [head, ...rest] = [...rows.filter(isHead), ...rows.filter((row) => !isHead(row))].map((row) => row.id)
  if (head === undefined) throw new Error(`no product row declares the ${scope} scope`)
  return [head, ...rest]
}

export const PLATFORM_SCOPE_REGISTRY = {
  "insurance-broker": {
    scope: "insurance-broker",
    label: "Cova",
    routePrefix: null,
    homePath: "/dashboard",
    app: "platform",
    launched: true,
    settingsPath: SCOPE_SETTINGS_HOMES["insurance-broker"].settings,
    // P-23: core has no help link either — the gap is the product's, not the
    // Underwriter product's.
    helpUrl: HELP_URL_NOT_YET_SUPPLIED,
    // Cova's own address, unchanged (P-22). Every product without one of its
    // own falls back to this.
    supportEmail: PLATFORM_SUPPORT_EMAIL,
    inboxSettingsPath: SCOPE_SETTINGS_HOMES["insurance-broker"].inboxes,
    // The name the agent's remedies have always used for this page.
    inboxSettingsLabel: "Settings → Integrations",
    calendarPath: "/calendar",
    bookingPath: "/calendar/booking",
    // The broker accept-invitation page (built with org/inviter query params
    // by the invite service).
    invitationSignupPath: null,
    // The broker forms workspace (004 §7). The one forms path literal outside
    // the core host's route table: every link is built from it.
    formsPath: "/forms",
    // Its ladder is Cova's own (`cova-tiers`), derived from `planPort` by
    // `planLadderOf` (018 §2.2): its plans ARE the ladder.
    tenancy: "caller",
    // README O4: core is an EXPLICIT product like any other, held by every
    // organisation through the H2 backfill. An absent header still resolves to
    // `insurance-broker` (S11) — what changed is that the broker product now has to be
    // granted, which is what makes an underwriter-only organisation unable to
    // read broker surfaces on the server rather than by a client redirect.
    grants: [{ kind: "holdsProduct" }],
    // The core FAMILY, canonical first, from the rows that declare the core
    // scope; `session-product.ts` DERIVES `CORE_FAMILY_AGENT_PRODUCTS` from it.
    // `assistant-underwriter` is among them: it is the broker deck's
    // underwriting mode, not the Underwriter product, and its row keeps it in
    // the core partition.
    products: familyOf("insurance-broker"),
    // Cova's own ladder: the one every tier, price and plan change is about.
    catalogueScope: "insurance-broker",
    // O10, as data. Symmetric with the underwriter row's entry.
    exclusiveWith: ["underwriter"],
    // The wall is the broker product's own: a payment method, a trial that
    // ended, a cancelled subscription. Derived `all-surfaces`: a billed port
    // with no floor (023 G57 (3)).
    paidRungPaths: [],
    // The one cascade rail: pricing scopes, seat groups, add-ons, networks.
    billingRail: "broker-cascade",
    // The broker plan; no floor: a firm whose subscription ends locks out.
    planPort: "insurance-broker",
    floor: null,
    // Its plan page is core's settings billing, not a product plan page.
    planPagePath: null,
    checkoutReturnPath: null,
    // The broker trial is the super-admin trial, not a declared policy (yet).
    trialPolicy: null,
    defaultFor: { organizationType: "brokerage" },
    // The broker product is where a personal mailbox lives by default.
    admitsPersonalConnections: true,
    // Broker organisations are created, never claimed.
    claimSource: null,
  },
  staff: {
    scope: STAFF_SCOPE,
    // The firm its members act under (022 G49 (1), STAFF-G5): Cova Staff.
    label: "Cova Staff",
    routePrefix: "/cs",
    // The staff shell's other section (`PLATFORM_STAFF_LINKS.superAdmin`): a tab
    // on it claims the staff firm, like `/cs` (STAFF-G5b, D49).
    sectionPrefixes: ["/super-admin"],
    homePath: "/cs",
    // The staff shell lives inside the platform (R0-6).
    app: "platform",
    launched: true,
    settingsPath: SCOPE_SETTINGS_HOMES.staff.settings,
    // Staff, who ask in Slack. Still declared, so the field is total.
    helpUrl: HELP_URL_NOT_YET_SUPPLIED,
    // Staff reach the same team by the same door; no second address to declare.
    supportEmail: SUPPORT_EMAIL_NOT_YET_SUPPLIED,
    inboxSettingsPath: SCOPE_SETTINGS_HOMES.staff.inboxes,
    inboxSettingsLabel: "Settings → Inboxes",
    calendarPath: "/cs/calendar",
    bookingPath: "/cs/calendar/booking",
    // Held beside core: its organisations invite through the broker page.
    invitationSignupPath: null,
    // The Customer section does not mount forms (004 §4.1): no forms link can
    // reach a `customer` form, so none may be made.
    formsPath: null,
    // Cova's ladder, and it is the surprising row. `/cs` runs against Cova's
    // OWN organisation and reads Cova's ladder (S33): port `none` billed
    // through the broker catalogue, so `planLadderOf` answers its catalogue's
    // ladder, `cova-tiers`. This is the row that rules the wall out as the
    // fact to key on: it is `"none"` here.
    // STAFF-G5 (022 G49 (2)): there is no platform org. A staff firm is the
    // tenant of its own `/cs` rows, like any firm of its own, so the row is
    // caller-tenanted and the home-org equality is gone. Any firm whose scope
    // is `staff` is a staff firm; nothing limits it to one.
    tenancy: "caller",
    // Admission (U15, D72): the ACTING firm's scope is `staff` (`holdsProduct`,
    // answered by `organisations.scope` through `actingFirmScopes`, 022 G48)
    // AND the person holds ANY live staff capability on that staff membership
    // (`staff_member_capabilities`, 022 G49 (1): one check). Each section or
    // agent product then checks its own key: `/cs` needs `cs.*`
    // (`staffSectionAccess`), curation and enrichment their own. The staff-
    // capability grant is also what marks the row as a staff firm's
    // (`isStaffFirmScope`).
    grants: [{ kind: "holdsProduct" }, { kind: "staffCapability", prefix: null }],
    // The rows that declare the customer scope. A unit test pins the family
    // against `CUSTOMER_SUCCESS_PRODUCT`.
    products: familyOf(STAFF_SCOPE),
    // NOT its own ladder — `/cs` is Cova staff using Cova's tools, and a staff
    // firm has no plan of its own to be measured against.
    // "Everyone is Max" in /cs; see the field's note.
    catalogueScope: "insurance-broker",
    // A staff firm holds one scope (022 G48); nothing to be exclusive with.
    exclusiveWith: [],
    // RUNBOOK D19: the Customer section is never plan-locked. Before 004 it
    // escaped the wall by accident — the platform org happens to hold a broker
    // billing account — and then by the "non-core is exempt" rule. Now port
    // `none` derives it: no wall (023 G57 (3)).
    paidRungPaths: [],
    // Billed through the platform organisation's core account (its
    // catalogueScope is core), never on a rail of its own.
    billingRail: "none",
    // Never billed of its own.
    planPort: "none",
    floor: null,
    planPagePath: null,
    checkoutReturnPath: null,
    // Never sold, so never trialled.
    trialPolicy: null,
    // Assigned to the platform organisation by a super-admin (G2), never minted
    // with an organisation.
    defaultFor: null,
    // See the field's note: the kernel already partitions a personal mailbox to
    // its owner, and CS staff work support and sales mail out of their own.
    admitsPersonalConnections: true,
    // Assigned to the platform organisation, never claimed.
    claimSource: null,
  },
  underwriter: {
    scope: "underwriter",
    label: "Underwriter",
    routePrefix: "/underwriter",
    // The prefix is a route group; the product's home is the Marketplace
    // (underwriters/product-v1/006 UW-N5). `/underwriter/profile` redirects here.
    homePath: "/underwriter/marketplace",
    app: "platform",
    launched: true,
    settingsPath: SCOPE_SETTINGS_HOMES.underwriter.settings,
    // P-23, the row the audit was about: `/underwriter`'s two refusals name a
    // human and link to nothing. The link renders the moment this is a URL.
    helpUrl: HELP_URL_NOT_YET_SUPPLIED,
    // P-22, the row this one is about: seven of this product's screens name
    // the BROKER product's support address. Set this and all seven change.
    supportEmail: SUPPORT_EMAIL_NOT_YET_SUPPLIED,
    inboxSettingsPath: SCOPE_SETTINGS_HOMES.underwriter.inboxes,
    inboxSettingsLabel: "Settings → Inboxes",
    calendarPath: "/underwriter/calendar",
    // SIBLING, not nested — see the field's docblock. This is the row the
    // concatenation got wrong.
    bookingPath: "/underwriter/booking",
    // The ticket-redeeming signup page (F7: lands on the post-auth welcome,
    // which sends a non-admin joiner home, UW-S9).
    invitationSignupPath: "/invitation/underwriter-signup",
    // The underwriter forms room (004 §7, §8.2), inside the product's prefix.
    formsPath: "/underwriter/forms",
    // ITS OWN LADDER (`underwriter-tiers`, derived from `planPort: "teams"` by
    // `planLadderOf`), never Cova's base/pro/max (UW-L1, lock U1): "Upgrade
    // this seat to Max" names a plan this customer cannot buy and links to a
    // core billing route they cannot reach.
    // A SOLD product: the buyer's own organisation is the tenant, so a run
    // partitions the buyer's engines. This is the row that re-opens S16 — the
    // "non-core implies platform tenancy" law was a Customer-era fact about the
    // one non-core scope, never a property of the primitive (README S27).
    tenancy: "caller",
    // One grant, and it is the holding. No staff capability: an underwriter
    // organisation's own members are not Cova staff, and gating their product on
    // a Cova capability key would mean nobody outside Cova could ever enter it.
    grants: [{ kind: "holdsProduct" }],
    products: familyOf("underwriter"),
    // A ladder of its own (003 §4.1): its own free base tier, its own keys.
    catalogueScope: "underwriter",
    exclusiveWith: ["insurance-broker"],
    // UW-L9 / DF4: a past-due or trial-ended Base organisation loses its Base
    // ENGINES and nothing else. The floor surfaces — the marketplace (profile,
    // messages) and settings (team, organisation, billing) — never wall, so
    // an admin can always reach the page they pay from. An organisation with
    // no billing account at all (every Free one) gets the gate's greenfield
    // `healthy` answer, so the pre-002 H13 spinner cannot come back. Derived
    // `paid-rungs`: a billed port with a floor (023 G57 (3)).
    //
    // The engine rooms, each `minTier: "base"` in the catalogue (UW-L4). The
    // same six routes as `ENGINE_ROOM_ROUTES` in the product's section slice,
    // which this domain file cannot import.
    paidRungPaths: [
      "/underwriter/agent",
      "/underwriter/mail",
      "/underwriter/calendar",
      "/underwriter/calls",
      "/underwriter/booking",
      "/underwriter/forms",
    ],
    // The rail key the `teams` port still answers to on the wire
    // (`RAIL_OF_PLAN_PORT`: `product` is `teams`), so it is not a second
    // answer beside `planPort`. Until the cutover it is also the rail that
    // bills: the product_plan_key ladder and the trials ledger, through the
    // legacy adapter (`LEGACY_PRODUCT_RAILS`). The key goes with
    // `BillingRail` at the contract (018 G39 (6)).
    billingRail: "product",
    // The Teams port with the Free floor (018 §2.2, 022 G50 (4)): no underwriter
    // port. Its rungs stay `free|base` (`PLAN_LADDERS`) until the cutover
    // commit moves them onto `free` + `underwriter_<rung>`, beside the re-key
    // release (v6.86) that re-keys the config rows and
    // `COVA_UNDERWRITER_PLAN_PORT=teams` that switches the port (018 P5, K3).
    planPort: "teams",
    floor: { key: "free", label: "Free" },
    // src/app/underwriter/(app)/welcome/plan/page.tsx, under routePrefix.
    planPagePath: "/welcome/plan",
    // src/app/underwriter/(app)/welcome/return/page.tsx, under routePrefix.
    checkoutReturnPath: "/welcome/return",
    // U9: 14 days of Base, card required, converts (003 UW-T1). The product
    // rail's offer, on its stored `base`. A trial start resolves its policy by
    // rung (`activeTrialPolicyFor`), so once `COVA_UNDERWRITER_PLAN_PORT` is
    // `teams` the start on `underwriter_base` takes `underwriter.base.v2` (the
    // manifest's `trialPolicyId`) without this row changing. The row moves to
    // v2 with the cutover commit that ends the legacy rail.
    trialPolicy: "underwriter.base.v1",
    defaultFor: { organizationType: "underwriter" },
    // O2 — Underwriter follows the CUSTOMER model: a walled product whose
    // engines are its own, with a person's mailbox exposed to it by an opt-in
    // membership row rather than by ownership. So the answer is the same answer
    // `customer` gives, for the same reason: `connection-access-queries.ts`
    // admits a personal connection on the owner arm or an explicit grant, never
    // on the scope arm alone, so exposing a mailbox here does not hand it to a
    // colleague. Underwriter staff work their submissions mail out of their own
    // inboxes; that is the job.
    admitsPersonalConnections: true,
    // D45: a marketplace listing is claimed when `claim_status = 'verified'`
    // with `claimed_by_org_id` set — the only state the claim, domain-claim and
    // self-submit paths write.
    claimSource: {
      table: "cova_underwriters",
      orgColumn: "claimed_by_org_id",
      statusColumn: "claim_status",
      claimedStatus: "verified",
    },
  },
  business: {
    scope: "business",
    label: "Business",
    // A business firm works in Cova Bots, on the Bots host (platform model,
    // ruled 2026-10-27); the platform mounts no business page, so no route
    // prefix claims a tab for it.
    routePrefix: null,
    // The platform's `/`: account-home routing (`homeFor`, 003 §9) sends a
    // firm that resolves no platform app to its Bots firm home,
    // `<bots origin>/o/<slug>/`. The row cannot name that URL itself (the
    // origin is the deployment's, the segment the firm's), and a platform path
    // into the broker's pages would walk the person out of the product.
    homePath: "/",
    // Cova Bots only, for now (platform model): a link built for a business
    // firm outside any app opens its Bots firm home on the Bots origin.
    app: "bots",
    launched: false,
    // Its settings, inbox, calendar and booking pages are Bots' own, inside
    // the firm's Bots home, so each points at the same `/` entry.
    settingsPath: "/",
    helpUrl: HELP_URL_NOT_YET_SUPPLIED,
    supportEmail: SUPPORT_EMAIL_NOT_YET_SUPPLIED,
    inboxSettingsPath: "/",
    inboxSettingsLabel: null,
    calendarPath: "/",
    bookingPath: "/",
    // Held beside the broker product on the platform: invites go through the
    // broker page.
    invitationSignupPath: null,
    // Business does not mount forms.
    formsPath: null,
    // Its own ladder, `teamsLadder("business")` =
    // business_base / _pro / _max / _autopilot (018 §2.1). Every per-rung row
    // keyed (scope "business", tier) (capability cells, model and bot
    // allocation defaults, usage tier config) was re-keyed base → business_base,
    // pro → business_pro, max → business_max by v6.46 (and seeded for
    // business_autopilot from Max by v6.49 and v7.01), since `isTierOfScope`
    // ignores a row off the ladder.
    //
    // A broker rung is never read onto this ladder (018 §2.3, R-022-3): a
    // business member resolves a paid feature only from a seat on this
    // ladder, so a seatless business member resolves no paid feature (fail
    // closed; app access still comes from the scope's app access cells).
    // `business-tiers` is derived from `planPort: "teams"` by `planLadderOf`;
    // no row declares a ladder.
    // The buyer's own organisation is the tenant, as for the broker product.
    tenancy: "caller",
    grants: [{ kind: "holdsProduct" }],
    // The business agent products (the `studio` agent product id is kept, R0-8).
    products: familyOf("business"),
    // Its own catalogue (018 P6, WP3.2.4, D41): business sells its Teams
    // ladder, so it is a catalogue product and a selling scope. Its
    // catalogue keys are the shared engines and agent actions it resolved
    // while it borrowed the broker ladder (`soldBy.business`, parity), and
    // v7.01 seeds its per-rung usage, model and bot rows (A9).
    catalogueScope: "business",
    // Held beside the broker product on the platform.
    exclusiveWith: [],
    // Own catalogue + Teams port + no floor derives `all-surfaces` (023 G55
    // (2)): the PaymentWall at `/payment` in Cova Bots. A pre-checkout firm is
    // walled only once its catalogue sells to it (`saleGatedWallStatus`, D37),
    // so before the business Teams prices exist in Stripe no firm is walled.
    paidRungPaths: [],
    // The legacy rail key stays `none` (D41 (b)): `product` is the retired
    // product-column rail, and its signup provisioning would trip
    // `billing_accounts_plan_key_check`. The plan port below is what sells
    // (`sellingScopes()` admits by `planPort`); the rail retires at P7.
    billingRail: "none",
    // Its port is already Teams, no floor (018 §2.2): `ladderFor("business")`
    // derives its ladder from this. A paid scope, so no seatless tier.
    planPort: "teams",
    floor: null,
    planPagePath: null,
    checkoutReturnPath: null,
    trialPolicy: null,
    // Granted by a super-admin, never minted with an organisation.
    defaultFor: null,
    // A person may expose their own mailbox to their Bots; the kernel still keeps
    // it theirs, as for every other product.
    admitsPersonalConnections: true,
    // Nobody claims into business.
    claimSource: null,
  },
} as const satisfies Readonly<Record<PlatformScope, ScopeManifest>>

/**
 * The real registry as the generic `ScopeRegistry<S>` every registry reader
 * takes, so a reader's `registry` parameter can default to it.
 *
 * THE ONE CAST, kept here so no call site carries its own. The registry is
 * `as const` (004 K1), so its rows are literal tuples typed by the three scopes
 * that exist; a reader generic over `S` (the dry run's world, 004 K11) cannot
 * see that type as `ScopeRegistry<S>` without widening. Every reader that
 * defaults to the real registry is only ever called with `S` inferred from a
 * real scope or left at `string`, and the rows satisfy `ScopeManifest` (the
 * `satisfies` above), so the widening is sound in every place it is used.
 */
export const defaultScopeRegistry = <S extends string = string>(): ScopeRegistry<S> =>
  PLATFORM_SCOPE_REGISTRY as unknown as ScopeRegistry<S>

/**
 * The products that SELL — the scopes whose catalogue ladder is their own
 * (004 K1). Derived from the rows, which is the whole point: `customer` is
 * excluded because its `catalogueScope` is `insurance-broker`, not because a second list
 * says so, and a product #4 joins by declaring its own ladder.
 *
 * The scopes that sell their own plans (spec multi-product 019 A1, OQ-019-2).
 * `feature-catalogue.ts` re-exports it for the entitlements side. It is what
 * makes `soldBy: { customer: … }` a compile error.
 */
export type SellingScope = {
  [S in PlatformScope]: (typeof PLATFORM_SCOPE_REGISTRY)[S]["catalogueScope"] extends S
    ? S
    : never
}[PlatformScope]

/**
 * The ladder a scope is measured against. `customer` reads `insurance-broker`'s.
 *
 * ═══ TWO OVERLOADS, ONE BODY (004 K11) ═══
 *
 * The default path keeps its NARROW return: `SellingScope` is a mapped
 * type over the real rows, and every caller that switches on the result depends
 * on it. The second overload is the dry run's — a registry with a product this
 * codebase has never heard of cannot answer a type computed from the three rows
 * that exist, so it answers `string`, which is the honest type for a world
 * assembled at runtime. Nothing has to pass a type argument either way.
 */
export function catalogueScopeOf(scope: PlatformScope): SellingScope
export function catalogueScopeOf<S extends string>(
  scope: S,
  registry: ScopeRegistry<S>,
): string
export function catalogueScopeOf(
  scope: string,
  registry: ScopeRegistry<string> = defaultScopeRegistry<string>(),
): string {
  return registry[scope]!.catalogueScope
}

/**
 * ═══ `NoInfer` IS WHAT KEEPS THE CHECK (004 K11) ═══
 *
 * These accessors take the registry as a defaulted parameter so the dry-run
 * harness can hand them a world with a product that does not exist. Written the
 * obvious way — `<S extends string>(scope: S, registry = …)` — that is a
 * LOOSENING: with the registry omitted, `S` infers from the ARGUMENT, so
 * `tenancyOf("studio")` compiles and then throws reading `undefined.tenancy`.
 * Before the parameter existed, `scope: PlatformScope` refused it outright.
 *
 * So `S` is inferred from the REGISTRY only (`NoInfer` on the scope) and
 * defaults to `PlatformScope`. Registry omitted ⇒ exactly today's check;
 * registry passed ⇒ that world's keys. `accessor-types.test.ts` holds the line
 * with a `@ts-expect-error` per accessor.
 */
/**
 * The product's UI noun — "Cova", "Underwriter", "Customer".
 *
 * Exists so a surface can render a product WITHOUT a scope literal: the
 * organisations list's badges, chips and stat cards all read this, which is
 * what lets the no-scope-literal ratchet hold over that slice.
 */
export const labelOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string => registry[scope].label

/**
 * The label of a RAW scope string (a holding, a billed-as product, a URL
 * param): the registry label when it names a scope, aliases read, else the
 * string itself (the rollout-skew case).
 */
export const labelOfRawScope = (raw: string): string =>
  Option.match(normalisePlatformScope(raw), { onNone: () => raw, onSome: (scope) => labelOf(scope) })

/** Whether the product is offered in the switcher and the rail (`ScopeManifest.launched`). */
export const isLaunched = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): boolean => registry[scope].launched

/** Where an admitted holder of this product lands. */
export const homePathOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string => registry[scope].homePath

/** The app a firm of this scope works in, whose origin its out-of-app links are built on (`ScopeManifest.app`). */
export const appOfScope = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): CovaAppLiteral => registry[scope].app

/**
 * What an identity must satisfy to enter this product (004 K1).
 *
 * Exported so the surface gate can say WHICH grant failed without reading the
 * row itself — the accessor seal is what keeps the next change to this relation
 * inside this file, and a rules module two doors down is still outside it.
 */
export const grantsOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): ReadonlyArray<PlatformScopeGrant> => registry[scope].grants

/**
 * Where this product's own settings live (G3).
 *
 * The redirect that sends a non-core holder off core `/settings` reads THIS,
 * never a product name, so a fourth product needs no edit at the redirect site.
 */
export const settingsPathOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string => registry[scope].settingsPath

/**
 * The path of the billing page of the row that SELLS this billing product
 * (spec underwriter/2026-10-17-001 D41): that row's `settingsPath + "/billing"`.
 *
 * A billing product is a catalogue product, and a catalogue product is the key
 * of the row that sells it, so the lookup is the row itself. The broker answers
 * `/settings/billing`, byte-identical to the URL the PM-setup email always
 * carried; a product #4 gets its own page by declaring its `settingsPath`. The
 * host is the caller's (an email needs an absolute URL, a link does not).
 */
export const billingPagePathOf = <S extends string = PlatformScope>(
  product: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string => `${registry[product].settingsPath}/billing`

/**
 * WHERE A PENDING PAYMENT SETUP SENDS THIS PRODUCT'S ADMIN, and which of its
 * surfaces the join gate never walls (spec underwriter/2026-10-17-001 R7, D10).
 *
 * `null` unless the row bills on a rail of its own AND declares a plan page:
 * a product with nowhere to pay has no setup to be taken to. Otherwise `page`
 * is `routePrefix + planPagePath`, and `exempt` holds the three setup
 * surfaces: the plan page, the checkout return (`routePrefix +
 * checkoutReturnPath`) and the billing page (`billingPagePathOf`). Read off
 * the row alone, so product #4 is gated by declaring its facts.
 */
export interface PaymentSetupSurface {
  readonly page: string
  readonly exempt: ReadonlyArray<string>
}

export const paymentSetupSurfaceOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): PaymentSetupSurface | null => {
  const row = registry[scope]
  if (row.billingRail === "none" || row.planPagePath === null || row.routePrefix === null) return null
  const page = `${row.routePrefix}${row.planPagePath}`
  const returnPage = row.checkoutReturnPath === null ? [] : [`${row.routePrefix}${row.checkoutReturnPath}`]
  return { page, exempt: [page, ...returnPage, billingPagePathOf(scope, registry)] }
}

/**
 * This product's help destination, or `null` while nobody has supplied one
 * (P-23). A surface renders the affordance only for a non-null answer — see the
 * field's docblock for why an invented URL is worse than the gap.
 */
export const helpUrlOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string | null => registry[scope].helpUrl

/**
 * This product's OWN support address, or `null` while it has none (P-22).
 *
 * Callers that render an address want `scopeSupportEmail` below; this is the
 * raw declaration, for a caller that needs to know whether one was declared.
 */
export const supportEmailOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string | null => registry[scope].supportEmail

/**
 * THE ADDRESS A SURFACE SHOWS: the product's own when it has one, the
 * platform's otherwise (P-22).
 *
 * The fallback is what makes this safe to wire everywhere today — an
 * underwriter mid-signup keeps the address they see now, rather than losing the
 * only contact on the screen while a decision is pending. It is ONE function so
 * that decision lands in one place, and so a surface cannot quietly choose the
 * other answer.
 */
export const scopeSupportEmail = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string => registry[scope].supportEmail ?? PLATFORM_SUPPORT_EMAIL

/**
 * Where a completed mailbox connect lands for this product (A18).
 *
 * The OAuth callback reads THIS. It must never build the path from
 * `routePrefix` or `settingsPath`: see the field's own docblock for what that
 * costs, and `insurance-broker` for why it was never a derivation.
 */
export const inboxSettingsPathOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string => registry[scope].inboxSettingsPath

/** What a sentence calls this product's inbox settings page, or `null` where it has none (P32). */
export const inboxSettingsLabelOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string | null => registry[scope].inboxSettingsLabel

/**
 * This product's own inbox settings page: `inboxSettingsPath` where it
 * differs from `homePath`, else null. A product with no settings pages yet
 * points its inbox settings at its home (business, today), so a surface that
 * offers to connect a mailbox has nowhere of its own to send the person, and
 * a path into another product's settings would walk them out (Cova Studio
 * P30).
 */
export const ownInboxSettingsPathOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string | null => {
  const inbox = registry[scope].inboxSettingsPath
  return inbox === registry[scope].homePath ? null : inbox
}

/**
 * This product's own booking page: `bookingPath` where it differs from
 * `homePath`, else null, by the rule `ownInboxSettingsPathOf` applies. The
 * agent's booking commands name it, and a product that points its booking
 * page at home is told the page isn't there yet (P32).
 */
export const ownBookingPathOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string | null => {
  const booking = registry[scope].bookingPath
  return booking === registry[scope].homePath ? null : booking
}

/**
 * Where this product's calendar workspace lives (A18, client half).
 *
 * The calendar's mode switch and its "Back to calendar" pill read THESE, never
 * `routePrefix` plus a broker path.
 */
export const calendarPathOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string => registry[scope].calendarPath

/**
 * Where this product's booking-links workspace lives (A18, client half).
 *
 * NOT `calendarPath + "/booking"`: `/underwriter/booking` is a SIBLING of the
 * calendar room, and the concatenation it replaces produced a 404.
 */
export const bookingPathOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string => registry[scope].bookingPath

/** Where a team invitation into this product's organisation lands, or `null` for the platform's page (E4b). */
export const invitationSignupPathOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string | null => registry[scope].invitationSignupPath

/**
 * Where this product mounts the forms engine, or `null` when it does not
 * (004 §7). Read by `formsRoutes` in the forms core leaf, which is the only
 * thing that should build a forms path from it.
 */
export const formsPathOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string | null => registry[scope].formsPath

/**
 * The invitation landing for an organisation holding `heldProducts` (E4b).
 *
 * Exactly ONE held product naming a path of its own ⇒ that path. Anything else
 * — no holdings (a failed read), only rows on the platform page, a product this
 * registry does not know, or two products naming different pages — ⇒ `null`,
 * the platform's accept-invitation page: today's behaviour, so an ambiguous or
 * missing answer never moves a brokerage's invitee.
 */
export const invitationSignupPathForHoldings = <S extends string = PlatformScope>(
  heldProducts: ReadonlyArray<string>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string | null => {
  const paths = new Set<string>()
  // Holdings are raw TEXT: a stored scope alias reads as its scope.
  for (const scope of heldProducts.map(canonicalScopeValue)) {
    if (!Object.hasOwn(registry, scope)) continue
    const path = invitationSignupPathOf(scope as S, registry)
    if (path !== null) paths.add(path)
  }
  return paths.size === 1 ? [...paths][0]! : null
}

/**
 * THE LADDER A SCOPE IS MEASURED AGAINST, AS A TYPE: `planLadderOf` at the type
 * level over the real registry row (018 §2.2: no row declares it).
 */
export type PlanLadderOf<S extends PlatformScope> =
  (typeof PLATFORM_SCOPE_REGISTRY)[S]["planPort"] extends "none"
    ? (typeof PLATFORM_SCOPE_REGISTRY)[S]["catalogueScope"] extends S
      ? "none"
      : LadderOfPort<
          (typeof PLATFORM_SCOPE_REGISTRY)[S]["catalogueScope"],
          (typeof PLATFORM_SCOPE_REGISTRY)[(typeof PLATFORM_SCOPE_REGISTRY)[S]["catalogueScope"]]["planPort"]
        >
    : LadderOfPort<S, (typeof PLATFORM_SCOPE_REGISTRY)[S]["planPort"]>

const ladderOfPort = (scope: string, port: PlanPortKey): PlanLadder => {
  switch (port) {
    case "insurance-broker":
      return "cova-tiers"
    // A Teams scope's own ladder. A scope no production type admits (the dry
    // run's) answers its own name, which only an injected ladder table holds.
    case "teams":
      return `${scope}-tiers` as PlanLadder
    case "none":
      return "none"
  }
}

/**
 * Which plan ladder this product's refusals may name (K6 follow-up (b)),
 * DERIVED from the row (018 §2.2, R-018-8: no row declares `planLadder`):
 *
 *  - a billed port names its ladder: `insurance-broker` → `cova-tiers`, `teams`
 *    → the scope's own `<scope>-tiers`;
 *  - port `none` reads the ladder of the catalogue it bills through
 *    (`customer` → the broker's `cova-tiers`, S33), and `none` when that is
 *    itself or is unbilled too.
 *
 * The rungs behind the name are `tiersForScope` (`plan-ladder.ts`).
 *
 * NOT the same question as `rolloutFlagsApply` — that one is about Cova's
 * staged RELEASE and lives with the engines, where the server gates import it
 * from. These agree on today's rows and are different facts.
 */
export const planLadderOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): PlanLadder => {
  const row = registry[scope]
  if (row.planPort !== "none") return ladderOfPort(scope, row.planPort)
  const catalogue = row.catalogueScope
  return catalogue === scope || !Object.hasOwn(registry, catalogue)
    ? "none"
    : ladderOfPort(catalogue, registry[catalogue].planPort)
}

/**
 * Which billing rail this product is sold on (uplift-01 002 §2.1). The one
 * reader for "how is this product billed": never infer it from `planLadderOf`.
 */
export const billingRailOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): BillingRail => registry[scope].billingRail

/** The kind of plan this scope is sold on (018 §2.2). `ladderFor` reads it. */
export const planPortOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): PlanPortKey => registry[scope].planPort

/** The seatless tier this scope's firms fall to, or `null` (018 §2.2). */
export const floorOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): PlanFloor | null => registry[scope].floor

/**
 * The products that SELL, in registry order (uplift-01 002 RF-5/RF-6): a scope
 * that is its own catalogue product and is sold on a plan port (`planPort` is
 * not `none`, 018 §2.2, D41 (b)): the port, not the legacy rail key, is what
 * sells, so `business` (Teams port, rail `none`) sells. The one derivation
 * behind every "selling scopes" list (the
 * Stripe catalogue report, the super-admin product switcher, the scoped
 * allocation products), so a product #4 joins all three by declaring its row.
 *
 * Lives here, not with the rungs: `plan-ladder.ts` imports this module, so a
 * rung import here would be a cycle. Same two-overload shape as
 * `catalogueScopeOf`: the real registry answers the narrow type, an injected
 * world answers `string`.
 */
export function sellingScopes(): ReadonlyArray<SellingScope>
export function sellingScopes<S extends string>(registry: ScopeRegistry<S>): ReadonlyArray<string>
export function sellingScopes(
  registry: ScopeRegistry<string> = defaultScopeRegistry<string>(),
): ReadonlyArray<string> {
  return Object.keys(registry).filter(
    (scope) =>
      catalogueScopeOf<string>(scope, registry) === scope &&
      planPortOf<string>(scope, registry) !== "none",
  )
}

/**
 * Which of this product's surfaces the billing wall covers (UW-L9), DERIVED
 * (023 G57 (3)): the port rule over the row's `planPort` and `floor` once the
 * scope sells on its own catalogue, else `none`. See `billing-wall.ts`. The
 * one reader every wall question goes through.
 */
export const billingWallPolicyOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): BillingWallPolicy => {
  const row = registry[scope]
  return scopeBillingWall({
    planPort: row.planPort,
    floor: row.floor,
    ownCatalogue: row.catalogueScope === scope,
  })
}

/**
 * Does the billing wall cover EVERY surface of this product?
 *
 * True for `"all-surfaces"` only — today's `true`, byte-identical for the
 * broker and Customer rows. A `"paid-rungs"` product answers `false` here
 * because the answer depends on WHERE in it a person stands; ask
 * `billingWallAppliesAt(pathname)` for that. Callers that must stay the broker
 * product's alone (the payment-method setup nudge) read this one.
 */
export const billingWallApplies = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): boolean => billingWallPolicyOf(scope, registry) === "all-surfaces"

/** The route prefixes a `"paid-rungs"` product walls. Empty for every other policy. */
export const paidRungPathsOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): ReadonlyArray<string> => registry[scope].paidRungPaths

/** The id of the trial policy this product sells, or `null` (UW-T1). */
export const trialPolicyOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): string | null => registry[scope].trialPolicy

/**
 * DOES THE BILLING WALL COVER THIS PATH? (UW-L9)
 *
 * The path is resolved to its product through `scopeForPath`, then the row's
 * policy answers: `"none"` never, `"all-surfaces"` always, `"paid-rungs"` only
 * on a declared paid-rung path (segment-boundary prefix match, the same rule
 * `scopeForPath` uses). No product literal: a fourth product is walled by its
 * row's billing facts alone.
 */
export const billingWallAppliesAt = <S extends string = PlatformScope>(
  pathname: string,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): boolean => {
  const scope = scopeForPath(pathname, registry)
  switch (billingWallPolicyOf(scope, registry)) {
    case "none":
      return false
    case "all-surfaces":
      return true
    case "paid-rungs":
      return registry[scope].paidRungPaths.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      )
  }
}

/**
 * Whose organisation a run in this scope belongs to.
 *
 * Read by `admitScope` (is the caller standing in the platform org?) and by
 * `grantRefusalFor` (may this organisation be given the product at all?). The
 * two are the same fact asked at two times, which is why there is no second
 * `requiresPlatformOrg` field for the second question (004 K1, design review).
 */
export const tenancyOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): PlatformScopeTenancy => registry[scope].tenancy

/**
 * IS A FIRM OF THIS SCOPE A STAFF FIRM? (STAFF-G5; 022 G49 (1)(2))
 *
 * True for the row whose admission demands a Cova staff capability: its
 * members act as Cova staff, under the firm's own tenancy. This replaced
 * `tenancy: "platform"` as the registry's mark for "Cova's own": there is no
 * platform org any more, and a staff firm is caller-tenanted like any firm, so
 * the mark is the GRANT the row declares, read back rather than restated.
 *
 * `string` in, own-property tested: an unknown scope is not a staff scope.
 */
export const isStaffFirmScope = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): boolean =>
  Object.prototype.hasOwnProperty.call(registry, scope) &&
  registry[scope].grants.some((grant) => grant.kind === "staffCapability")

/**
 * Is this organisation a staff firm, asked of its scope(s)?
 *
 * The registry-derived form of "is this Cova's own firm", for the rules that
 * must answer it from a SCOPE rather than a literal: organisation lifecycle,
 * whose refusal is "the staff firm cannot be suspended" (spec
 * super-admin/2026-09-18-001 §2.5). The name is kept for its three callers
 * until ONE-11 retires the platform-org vocabulary; the answer is the staff
 * firm's (`isStaffFirmScope`), not a tenancy (STAFF-G5).
 *
 * `string[]`, and UNKNOWN SCOPES ARE SKIPPED rather than thrown on: the stored
 * value is TEXT and a newer deploy can write one this build lacks. A scope this
 * build has never heard of is not the staff scope as far as it can tell.
 */
export const holdsPlatformTenancy = (scopes: ReadonlyArray<string>): boolean =>
  normalisePlatformScopes(scopes).some((scope) => isStaffFirmScope(scope))

/**
 * THE STAFF FIRM'S SCOPE (spec v1.5.5/002; STAFF-G5).
 *
 * A service that exists to serve Cova's own staff product needs to name its
 * scope, and `const X: PlatformScope = "staff"` in a shared tree is exactly
 * what 004 K7's ratchet refuses. The fact is IN the registry: the row that
 * gates on a staff capability is the staff firm's (`isStaffFirmScope`).
 * Reading it back is derivation, not a hard-code wearing a helper's name. It
 * replaced `platformTenancyScope`, which read `tenancy: "platform"`, a value no
 * row carries since there is no platform org (022 G49 (2)).
 *
 * THROWS ON AMBIGUITY, deliberately. Exactly one row is a staff firm's today.
 * If a second ever is, every caller has a real question to answer (WHICH staff
 * product is this service for), and a silent `find()` would answer it by
 * registry declaration order. Failing at startup with the candidates named is
 * the honest behaviour.
 */
export const staffFirmScope = <S extends string = PlatformScope>(
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): S => {
  // `as unknown as` for the same reason the default argument above needs it:
  // `Object.keys` is typed `string[]` whatever the object, and `S` is a narrower
  // string. This is the file's existing idiom, not a new liberty.
  const candidates = (Object.keys(registry) as unknown as ReadonlyArray<S>).filter((scope) =>
    isStaffFirmScope(scope, registry),
  )
  if (candidates.length !== 1) {
    throw new Error(
      `staffFirmScope: expected exactly one product gated on a staff capability, found ${candidates.length}` +
        (candidates.length === 0 ? "" : ` (${candidates.join(", ")})`) +
        ". A caller asking for 'the staff product' has a real question to answer when there is more than one.",
    )
  }
  return candidates[0]!
}

/** The products this one may not be held alongside (O10 as data). */
export const exclusiveWithOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): ReadonlyArray<S> => registry[scope].exclusiveWith

/** The organisation type minted into this product, if any (E1). */
export const defaultOrganizationTypeOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): OrganizationType | null => registry[scope].defaultFor?.organizationType ?? null

/** The claim table the product declares, or `null` when nobody claims into it (D45). */
export const claimSourceOf = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): ClaimSource | null => registry[scope].claimSource

/**
 * Longest matching route prefix wins; anything unmatched is `insurance-broker`.
 *
 * The default path returns `PlatformScope` — the identity provider keys its
 * query on the result and every consumer narrows on it. Handed another registry
 * (004 K11) it answers that world's scope as a `string`, because a world
 * assembled at runtime cannot answer a union computed from the real rows.
 */
export function scopeForPath(pathname: string): PlatformScope
export function scopeForPath<S extends string>(
  pathname: string,
  registry: ScopeRegistry<S>,
): S
export function scopeForPath(
  pathname: string,
  registry: ScopeRegistry<string> = defaultScopeRegistry<string>(),
): string {
  let best: { readonly scope: string; readonly prefix: string } | null = null
  for (const definition of Object.values(registry)) {
    // `routePrefix` and the row's further sections (`sectionPrefixes`) alike.
    const prefixes = [
      ...(definition.routePrefix === null ? [] : [definition.routePrefix]),
      ...(definition.sectionPrefixes ?? []),
    ]
    for (const prefix of prefixes) {
      if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
        if (best === null || prefix.length > best.prefix.length) best = { scope: definition.scope, prefix }
      }
    }
  }
  return best?.scope ?? DEFAULT_PLATFORM_SCOPE
}

/** Does ONE declared grant hold for this identity? */
const grantHolds = (
  scope: string,
  grant: PlatformScopeGrant,
  identity: ScopeAdmissionIdentity,
): boolean =>
  grant.kind === "holdsProduct"
    ? identity.heldProducts.includes(scope)
    : hasStaffCapability(identity, grant.prefix)

/**
 * README S12, as derived from the row (S27): every declared grant must hold,
 * and a `platform`-tenancy row additionally requires the caller's HOME org to
 * BE the platform org.
 *
 * The tenancy check is not a grant because it is not about the person: it is the
 * statement that this product's rows belong to Cova, which is what makes a
 * staffer's home brokerage the wrong tenant for a Customer run.
 *
 * The registry arrives as a DEFAULTED parameter (004 K1) so the dry-run harness
 * can ask this same function about a product that does not exist. Production
 * never passes it.
 */
export const admitScope = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  identity: ScopeAdmissionIdentity,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): boolean => {
  /**
   * ═══ UNKNOWN ⇒ NOT ADMITTED, NEVER A DEFECT (audit A5) ═══
   *
   * This is the one accessor in this file deliberately fed values from OUTSIDE
   * the type system — a stored `organisations.scope` value — and the one where the wrong answer is an
   * access-control answer. `registry[scope]` for a scope no row declares is
   * `undefined`, and reading `.grants` off it throws a `TypeError`: a DEFECT,
   * not a refusal. A defect on a gate lane does not answer `false` — it becomes
   * a 500, or is swallowed by a surrounding `catchAll` that reads an error as
   * "not resolved yet" and renders a waiting surface to somebody who should
   * have been refused.
   *
   * `NoInfer` closes it at compile time for callers inside the type system
   * (the seam this file's own header describes, and the one admission had
   * escaped); this line closes it for the header, which is not.
   *
   * AN OWN-PROPERTY TEST, not `=== undefined`. `registry["__proto__"]` answers
   * `Object.prototype` and `registry["constructor"]` answers a function —
   * neither is `undefined`, and both then throw on `.grants`. A header carries
   * whatever the caller sent, so the check has to be about what this registry
   * DECLARES rather than about what the lookup happened to return.
   */
  if (!Object.prototype.hasOwnProperty.call(registry, scope)) return false
  const row = registry[scope]
  if (!row.grants.every((grant) => grantHolds(scope, grant, identity))) return false
  return row.tenancy === "platform"
    ? identity.platformOrgId != null && identity.platformOrgId === identity.orgId
    : true
}

/**
 * May a personal mailbox be exposed to this scope? Read from the registry so
 * the answer lives beside the scope it is about — never re-derived from
 * `ownerType` at a call site (see `admitsPersonalConnections`).
 */
export const scopeAdmitsPersonalConnections = (scope: PlatformScope): boolean =>
  PLATFORM_SCOPE_REGISTRY[scope].admitsPersonalConnections

/**
 * Parse a raw header value. `None` for an unknown value (the middleware maps it
 * to `PLATFORM_SCOPE_INVALID`); `Some(core)` for absent / empty.
 */
export const parseScopeHeader = (raw: string | null | undefined): Option.Option<PlatformScope> => {
  const trimmed = raw?.trim() ?? ""
  if (trimmed.length === 0) return Option.some(DEFAULT_PLATFORM_SCOPE)
  return decodePlatformScope(trimmed)
}

/**
 * A scope that came out of `admitRequestScope` — the app's narrowing of the
 * package's framework-free `AdmittedScope` (spec v1.5.5/003 K1, lock S38).
 *
 * A branded literal is a SUBTYPE of `PlatformScope`, so every `scope:
 * PlatformScope` service input accepts one unchanged: the brand costs one line
 * at the mint and nothing at any read.
 */
export type AdmittedPlatformScope = PlatformScope & AdmittedScope

/**
 * An identity as a handler receives it. The app-side spelling of the package's
 * `ScopeAdmitted`, carrying the CLOSED scope union rather than `string`, which
 * is why app-owned tags' readers can write `identity.scope` with no decode.
 */
export type ScopeAdmitted<I> = I & { readonly scope: AdmittedPlatformScope }

/**
 * Narrow the package tag's `AdmittedScope` (a branded `string`) to the closed
 * union — the ONE read that `@cova/agent-v2-core`'s inability to name
 * `PlatformScope` costs us (003 §2.3).
 *
 * DECODE OR DIE, never default. The value is branded, so it can only have come
 * from the admission, and the admission only ever returns a member of the
 * union: a miss here is not a caller mistake to absorb, it is a forged brand or
 * a registry that changed under a running process. `scopeOf`'s fallback exists
 * for carriers whose TYPE cannot promise a value; this one's can.
 */
export const admittedScopeOf = (carrier: {
  readonly scope: AdmittedScope
}): AdmittedPlatformScope =>
  /*
   * RETURNS THE BRAND, and this is a narrowing rather than a mint (spec
   * v1.5.5/004 K6). The runtime value is unchanged: the decode reads a string
   * that already carries `AdmittedScope`, and what comes back IS that same
   * value. Only the declared type stops throwing the brand away.
   *
   * It matters because 004's voucher has an `admitted` arm whose input type is
   * `AdmittedPlatformScope`. Before this line, the one function that reads an
   * admitted carrier handed back a bare `PlatformScope`, so the RPC lane could
   * not build that arm without re-minting — and re-minting is what 003 K1's
   * "exactly two files" forbids. Every existing caller wants a `PlatformScope`
   * and a subtype satisfies them, so nothing else changes.
   */
  Option.getOrElse(decodePlatformScope(carrier.scope), () => {
    throw new Error(
      "admittedScopeOf: an admitted scope decoded to no known product. `admitRequestScope` cannot produce this, so the brand was forged or the registry changed under a live process.",
    )
  }) as AdmittedPlatformScope

/**
 * The scope an identity or principal carries, narrowed from the framework-free
 * `string` the contracts store. Absent or unknown ⇒ `insurance-broker` — the identity
 * middleware has already refused an unknown header, so an unknown value here
 * can only be a literal constructed without a scope.
 *
 * ═══ IT REFUSES AN ADMITTED CARRIER, BY TYPE (003 K6) ═══
 *
 * The conditional parameter type resolves to `never` when the argument's
 * `scope` is an `AdmittedScope`, so `scopeOf(identity)` on anything a request
 * identity middleware provides is a compile error. That is not pedantry: on
 * those carriers the fallback was the bug. An org-admin lane that never
 * admitted a scope handed `undefined` here, `undefined` read as `insurance-broker`, and a
 * `/cs` admin was served the brokerage's rows by a partition that was working
 * perfectly (v1.5.5/001, the owner's screenshot). Writing `identity.scope`
 * cannot express that failure, because the type has no `undefined` in it.
 *
 * S26 STANDS for everything else. Rows read from a `TEXT` column, `ToolContext`
 * on a headless run, `CovaCliCaller` — their types genuinely cannot promise a
 * value, so the read must say what absence means, and this is where it says it.
 * Those carriers hold `scope: string`, never the brand, so the conditional
 * resolves to `C` and they are unaffected.
 */
export const scopeOf = <C extends { readonly scope?: string | null | undefined }>(
  carrier: C extends { readonly scope: AdmittedScope } ? never : C,
): PlatformScope =>
  Option.getOrElse(decodePlatformScope(carrier.scope ?? undefined), () => DEFAULT_PLATFORM_SCOPE)

/**
 * The scope ↔ agent product relation, read from the registry (never a second
 * table). The CANONICAL product of a scope is its family's head, which is the
 * PLATFORM app's canonical row (006 §2.2: the one-argument form means
 * `platform`). A reader that serves another app asks `agentProductFor(app,
 * scope)` in `cova-apps.ts`.
 */
export const agentProductForScope = (scope: PlatformScope): string =>
  PLATFORM_SCOPE_REGISTRY[scope].products[0]

/**
 * The DEFAULTING product → scope lookup: absent or unknown ⇒
 * `insurance-broker`. Only the usage ladder reads it (006 §2.3): an unknown
 * product is measured on the broker ladder, never free. Everything that
 * decides what a run may do, or which partition it reads, uses
 * `scopeForKnownAgentProduct` / `scopeForStoredAgentProduct` and refuses the
 * unknown; R-006-2 (`scope-for-agent-product-ratchet.test.ts`) holds every
 * other production call at zero.
 */
export function scopeForAgentProduct(product: string | null | undefined): PlatformScope
export function scopeForAgentProduct<S extends string>(
  product: string | null | undefined,
  registry: ScopeRegistry<S>,
): S
export function scopeForAgentProduct(
  product: string | null | undefined,
  registry: ScopeRegistry<string> = defaultScopeRegistry<string>(),
): string {
  if (product === null || product === undefined) return DEFAULT_PLATFORM_SCOPE
  for (const definition of Object.values(registry)) {
    // WIDENED AT THE READ, on purpose. The registry is `as const` (004 K1) so a
    // row's `products` is a tuple of LITERALS, and `includes` on a union of such
    // tuples asks for a value assignable to every one of them at once — which is
    // `never`. The question here is genuinely about an arbitrary string (the
    // product a session carries, which may be one no row has heard of), so the
    // honest move is to read the family as the list of strings it is. Not a
    // cast: a readonly tuple of literals IS a `ReadonlyArray<string>`.
    const family: ReadonlyArray<string> = definition.products
    if (family.includes(product)) return definition.scope
  }
  return DEFAULT_PLATFORM_SCOPE
}

/**
 * The scope of a product the registry KNOWS, or `none` (006 §2.3). Writers and
 * gates that decide what a run may do read this, never `scopeForAgentProduct`:
 * that one maps an unknown or absent product to `insurance-broker`, so a
 * product nobody registered (or a `bots-*` row whose scope family has not
 * claimed it yet) would be run, gated and sold as a broker product. Here the
 * unknown is `none`, and the caller refuses it.
 *
 * An id a row declares as an ALIAS (`eval-sandbox`, `eval-run-lite`, and
 * `studio` once it aliases `bots-business`) reads as its row: 004 DT-11's
 * CHECK admits stored aliases, so they are known. Absent is `none` too; a
 * caller holding a stored session row whose column may still be NULL reads it
 * through `scopeForStoredAgentProduct`.
 */
export const scopeForKnownAgentProduct = (product: string | null | undefined): Option.Option<PlatformScope> => {
  if (product === null || product === undefined) return Option.none()
  const canonical = AGENT_PRODUCT_ROWS.find((row) => row.aliases.includes(product))?.id ?? product
  return Option.fromNullable(
    PLATFORM_SCOPES.find((scope) => {
      const family: ReadonlyArray<string> = PLATFORM_SCOPE_REGISTRY[scope].products
      return family.includes(canonical)
    }),
  )
}

/**
 * The product a stored session with a NULL `product` reads as: the sidebar,
 * as every reader already treats it (006 §2.4 step 3). Goes when the column
 * is NOT NULL (§2.4 step 5).
 */
const NULL_STORED_AGENT_PRODUCT: AgentProduct = "sidebar"

/**
 * `scopeForKnownAgentProduct` for a product read off a stored session row,
 * where NULL still means the sidebar until §2.4 backfills the column. A
 * non-NULL unknown id stays `none`.
 */
export const scopeForStoredAgentProduct = (product: string | null | undefined): Option.Option<PlatformScope> =>
  scopeForKnownAgentProduct(product ?? NULL_STORED_AGENT_PRODUCT)

/**
 * The FAMILY a scope declares — every agent product whose runs belong to that
 * product surface, canonical first (spec customer-success/v1.5.2/006 M5, as
 * widened by v1.5.4/002 H4).
 *
 * It used to filter a `knownProducts` tuple handed in by the caller, because the
 * row carried one `agentProduct` and membership had to be computed. The row now
 * DECLARES the family, so the list is read rather than derived — which is what
 * lets `session-product.ts` derive `CORE_FAMILY_AGENT_PRODUCTS` from here
 * instead of maintaining a second copy, and what makes "every known product in
 * exactly one family" an assertion about one place.
 */
export const productsForScope = <S extends string = PlatformScope>(
  scope: NoInfer<S>,
  registry: ScopeRegistry<S> = defaultScopeRegistry<S>(),
): ReadonlyArray<string> => registry[scope].products

/**
 * EVERY agent product any registry row declares, as the TYPE the rows declare
 * (F-13). The registry is `as const satisfies …` (004 K1), so each row's
 * `products` is a tuple of literals and this union is the manifest's own answer
 * rather than a copy of it.
 *
 * `KnownAgentProduct` in `session-product.ts` used to be
 * `(typeof KNOWN_AGENT_PRODUCTS)[number]` over a HAND-WRITTEN tuple, pinned to
 * the registry by a runtime test only. A hand-written tuple plus a runtime pin
 * catches a fourth product the day someone runs the suite; a derived union
 * cannot be wrong at all, and `agent-product-registration-ratchet.test.ts` now
 * proves the refusal with a `@ts-expect-error` instead of asserting a copy.
 */
export type AgentProduct =
  (typeof PLATFORM_SCOPE_REGISTRY)[PlatformScope]["products"][number]

/**
 * The same set as a VALUE, in registry order (each scope's family, canonical
 * first). The callback is annotated so the element type is the union above and
 * not `string` — that annotation is the whole reason a consumer can hold this
 * as `ReadonlyArray<AgentProduct>` without a cast.
 */
export const ALL_AGENT_PRODUCTS: ReadonlyArray<AgentProduct> = PLATFORM_SCOPES.flatMap(
  (scope): ReadonlyArray<AgentProduct> => PLATFORM_SCOPE_REGISTRY[scope].products,
)

/**
 * How a SQL predicate filters `agent_sessions.product` for one app in one
 * scope (006 §2.2, AG-3; 003 §8): always an INCLUSION of the (scope, app)
 * include-list (`familyFor`), so a product of another app in the same scope,
 * another scope's product, a new row, or an unknown id never reaches the
 * surface. There is no exclusion form: the old broker exclusion pooled every
 * product nobody had declared into broker memory.
 *
 * `admitsUnstamped` says whether a NULL `product` belongs to this surface. A
 * NULL reads as the sidebar (`NULL_STORED_AGENT_PRODUCT`, the value §2.4 step 3
 * backfills), so exactly the include-list that holds the sidebar admits it and
 * no other does. TODO(WP0.21.4): drop the field once the backfill (WP0.21.3,
 * gated on the C-3 counts, WP0.21.1) has run and the column is NOT NULL
 * (§2.4 step 5); the predicate is then `product IN (...)` alone.
 */
export interface AgentProductIncludeFilter {
  readonly products: ReadonlyArray<AgentProductId>
  readonly admitsUnstamped: boolean
}

export const agentProductFilter = (scope: PlatformScope, app: CovaAppLiteral): AgentProductIncludeFilter => {
  const products = familyFor(scope, app)
  return { products, admitsUnstamped: products.some((id) => id === NULL_STORED_AGENT_PRODUCT) }
}
